import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { authService } from '../../services/authService';
import { Config, isDemoMode } from '../../config';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [demoConfigError, setDemoConfigError] = useState(false);

  const isDemoParam = searchParams.get('demo') === 'true';
  const [showDemoLanding, setShowDemoLanding] = useState(false);

  useEffect(() => {
    checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle redirect response on component mount for Azure AD
  useEffect(() => {
    const handleRedirectResponse = async () => {
      try {
        // Wait a bit for the authService to process the redirect
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Re-check authentication after redirect
        await checkAuthentication();
      } catch (error) {
        console.error('Error handling redirect response:', error);
      }
    };

    // Check if we're returning from a redirect (URL contains code or state parameters)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('code') || urlParams.has('state')) {
      handleRedirectResponse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthentication = async () => {
    try {
      // Use the refresh method to ensure we have the latest authentication state
      const authenticated = await authService.refreshAuthenticationState();
      // eslint-disable-next-line no-console
      console.log('Authentication check result:', authenticated);

      if (!authenticated) {
        if (isDemoMode) {
          // In demo mode, redirect to login page
          navigate('/login', { replace: true });
          return;
        }

        // ?demo=true — show landing page with credentials before redirecting
        if (isDemoParam) {
          if (!Config.auth.demoLoginHint) {
            // VITE_DEMO_LOGIN_HINT is not set — fail loudly rather than silently falling back
            setDemoConfigError(true);
            return;
          }
          setShowDemoLanding(true);
          return;
        }

        // Standard production login — no hint
        // eslint-disable-next-line no-console
        console.log('User not authenticated, redirecting to Microsoft sign-in');
        try {
          await authService.login();
        } catch (error) {
          console.error('Login failed:', error);
        }
        return;
      }

      setIsAuthenticated(authenticated);
    } catch (error) {
      console.error('Authentication check failed:', error);
      setIsAuthenticated(false);
      if (isDemoMode) {
        navigate('/login', { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const handleDemoSignIn = async () => {
    try {
      await authService.login(Config.auth.demoLoginHint!);
    } catch (error) {
      console.error('Demo login redirect failed:', error);
    }
  };

  if (showDemoLanding) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={3}
        sx={{ p: 4, bgcolor: 'background.default' }}
      >
        <Card sx={{ maxWidth: 440, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              GovIQ CallScore Demo
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Sign in with the demo account below to explore the platform.
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  EMAIL
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {Config.auth.demoLoginHint}
                </Typography>
              </Box>
              {Config.auth.demoPasswordHint && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    PASSWORD
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {Config.auth.demoPasswordHint}
                  </Typography>
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleDemoSignIn}
            >
              Sign In to Demo
            </Button>
          </CardContent>
        </Card>
        <Typography variant="caption" color="text.secondary">
          The email will be pre-filled on the Microsoft sign-in screen.
        </Typography>
      </Box>
    );
  }

  if (demoConfigError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        sx={{ p: 4 }}
      >
        <Alert severity="error">
          Demo login is not configured. VITE_DEMO_LOGIN_HINT is not set in the application
          settings. Contact your administrator.
        </Alert>
      </Box>
    );
  }

  // If not authenticated, the checkAuthentication function will handle the redirect
  // We only render children if authenticated
  if (!isAuthenticated) {
    if (isDemoMode) {
      // In demo mode, the navigation to /login is handled in checkAuthentication
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          gap={2}
        >
          <CircularProgress />
          <Typography>Redirecting to login...</Typography>
        </Box>
      );
    } else {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          gap={2}
        >
          <CircularProgress />
          <Typography>Redirecting to Microsoft sign-in...</Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};
